import { useCallback, useEffect, useMemo } from 'react';

import { toaster } from 'components/toaster/toaster';
import { ServiceMode } from 'generated/graphql-gateway';
import { t } from 'state/intl/translations';
import { useFlag } from 'state/launchdarkly';
import { LaunchDarklyFlag } from 'state/launchdarkly';
import { useServiceModeContext, useServiceModeLive } from 'state/service-mode';
import logger from 'utils/logger';
import { getHeadingForServiceMode } from 'utils/service-mode';

import { useStoreContext } from '..';
import { getRestaurantLatestClosingTime } from '../utils';

import { useStoreAvailabilityFns } from './use-store-availability-fns';

const SERVICE_MODE_ORDER: ServiceMode[] = [
  ServiceMode.TAKEOUT,
  ServiceMode.DRIVE_THRU,
  ServiceMode.MOBILE_ORDER_DRIVE_THRU,
  ServiceMode.EAT_IN,
];

export type ServiceModeConfig = {
  heading: string;
  type: ServiceMode;
  isSelected: boolean;
  isAvailable: boolean;
  isServiceModeDisabledByFlag: boolean;
};

/**
 * This hook is used to get and select the next available service mode for the pickup service mode fallback
 * when the selected store is unavailable by service mode.
 *
 * Service mode can be disable by flag or by unavailable operating hours.
 *
 */
export const usePickupServiceModeFallback = (): {
  selectNextAvailableServiceMode: (nextAvailableServiceMode: ServiceModeConfig) => void;
  selectedServiceModeConfig: ServiceModeConfig | undefined;
  getNextAvailableServiceMode: () => ServiceModeConfig | false;
} => {
  const activeServiceMode = useServiceModeLive();
  const { selectServiceMode } = useServiceModeContext();
  const isDelivery = activeServiceMode === ServiceMode.DELIVERY;
  const { store } = useStoreContext();

  const isFastLaneEnabled = useFlag(LaunchDarklyFlag.ENABLE_FAST_LANE_DRIVE_THRU);
  const isMobileOrderForDriveThruLaneEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_MOBILE_ORDER_DRIVE_THRU_LANE
  );

  const { isServiceModeAvailable, isServiceModeDisabled } = useStoreAvailabilityFns();

  const serviceModesConfig = useMemo((): ServiceModeConfig[] => {
    return SERVICE_MODE_ORDER.flatMap(serviceMode => {
      const serviceModeHeading = getHeadingForServiceMode(
        isFastLaneEnabled,
        isMobileOrderForDriveThruLaneEnabled,
        serviceMode
      );

      return {
        heading: serviceModeHeading?.heading ?? '',
        type: serviceMode,
        isSelected: activeServiceMode === serviceMode,
        isAvailable: isServiceModeAvailable(store, serviceMode),
        isServiceModeDisabledByFlag: isServiceModeDisabled(store, serviceMode),
      };
    });
  }, [
    isFastLaneEnabled,
    isMobileOrderForDriveThruLaneEnabled,
    activeServiceMode,
    isServiceModeAvailable,
    store,
    isServiceModeDisabled,
  ]);

  const selectedServiceModeConfig = useMemo(
    () => serviceModesConfig.find(serviceMode => serviceMode.type === activeServiceMode),
    [serviceModesConfig, activeServiceMode]
  );

  const getNextAvailableServiceMode = useCallback((): ServiceModeConfig | false => {
    if (!activeServiceMode) {
      return false;
    }

    const excludeServiceModes = serviceModesConfig
      .filter(serviceMode => serviceMode.isServiceModeDisabledByFlag || !serviceMode.isAvailable)
      .map(serviceMode => serviceMode.type);

    const latestServiceMode = getRestaurantLatestClosingTime({
      store,
      serviceModes: 'pickup',
      include24HourServiceModes: true,
      storeLocalTime: store.localTime || '',
      excludeServiceModes,
    });

    if (latestServiceMode && latestServiceMode.serviceMode !== activeServiceMode) {
      const nextAvailableServiceMode = serviceModesConfig
        .filter(serviceMode => !excludeServiceModes.includes(serviceMode.type))
        .find(serviceMode => serviceMode.type === latestServiceMode.serviceMode);

      if (nextAvailableServiceMode) {
        logger.info({
          message: 'Found next available service mode',
          currentServiceMode: activeServiceMode,
          nextServiceMode: nextAvailableServiceMode.type,
          nextServiceModeHeading: nextAvailableServiceMode.heading,
          reason: 'Latest closing time service mode',
        });
        return nextAvailableServiceMode;
      }
    }

    logger.info({
      message: 'No next available service mode found, returning false',
      activeServiceMode,
      latestServiceMode: latestServiceMode?.serviceMode,
      hasLatestServiceMode: !!latestServiceMode,
      excludeServiceModes,
      serviceModes: serviceModesConfig,
    });

    return false;
  }, [activeServiceMode, serviceModesConfig, store]);

  const selectNextAvailableServiceMode = useCallback(
    (nextAvailableServiceMode: ServiceModeConfig) => {
      if (!nextAvailableServiceMode) {
        return;
      }

      selectServiceMode(nextAvailableServiceMode.type);
      toaster.informative(t('serviceModeSwitchedAutomatically'));

      logger.info({
        message: 'Service mode selection completed',
        selectedServiceMode: nextAvailableServiceMode.type,
        toastMessage: `${selectedServiceModeConfig?.heading} is unavailable. Switched to ${nextAvailableServiceMode.heading}. You can change it if needed.`,
      });
    },
    [selectServiceMode, selectedServiceModeConfig?.heading]
  );

  useEffect(() => {
    if (isDelivery) {
      return;
    }

    if (!selectedServiceModeConfig?.isAvailable) {
      const nextAvailableServiceMode = getNextAvailableServiceMode();
      if (nextAvailableServiceMode) {
        selectNextAvailableServiceMode(nextAvailableServiceMode);
      }
    }
  }, [
    selectedServiceModeConfig,
    getNextAvailableServiceMode,
    selectNextAvailableServiceMode,
    isDelivery,
  ]);

  return {
    selectNextAvailableServiceMode,
    selectedServiceModeConfig,
    getNextAvailableServiceMode,
  };
};
